import React, { useState } from "react";

import { UseInfiniteQueryResult } from "@tanstack/react-query";
import { useTranslation } from "next-i18next";
import { RecipeApiNS } from "~types/api/recipe";
import { Recipe } from "~types/recipe";

import { AddToCartDrawer } from "@/components/blocks/global/add-to-cart-drawer/AddToCartDrawer";
import { AddToFolderDrawer } from "@/components/blocks/global/add-to-folder-drawer/AddToFolderDrawer";
import RecipePan from "@/components/blocks/global/recipe-pan/RecipePan";
import { ArrowButton } from "@/components/elements/arrow-button/ArrowButton";
import {
  Box,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";

import ArrowLeftLong from "~public/icons/Arrow Left Long/Arrow Left Long.svg";
import ArrowLeftSmall from "~public/icons/Arrow Left Small/Arrow Left Small.svg";
import ArrowRightLong from "~public/icons/Arrow Right Long/Arrow Right Long.svg";
import ArrowRightSmall from "~public/icons/Arrow Right Small/Arrow Right Small.svg";
import NotFoundIcon from "~public/icons/Not Found/Not found.svg";

import RecipePanSelectable from "../recipe-pan-selectable/RecipePanSelectable";

import GetRecipesResponse = RecipeApiNS.GetRecipesResponse;

interface UnselectableRecipesResultsProps {
  pageSize: number;
  useQueryResult: UseInfiniteQueryResult<GetRecipesResponse>;
  selectable?: false;
  selectedRecipesArray?: undefined;
  setSelectedRecipesArray?: undefined;
}

interface SelectableRecipesResultsProps {
  pageSize: number;
  useQueryResult: UseInfiniteQueryResult<GetRecipesResponse>;
  selectable: true;
  selectedRecipesArray: Recipe["pk"][];
  setSelectedRecipesArray: Function;
}

type RecipesResultsProps =
  | UnselectableRecipesResultsProps
  | SelectableRecipesResultsProps;

interface IAddToCartContext {
  setDrawerData: React.Dispatch<
    React.SetStateAction<Pick<Recipe, "pk" | "convenience_level"> | null>
  >;
  handleOpenDrawer: () => void;
  handleOpenFolderDrawer: () => void;
}

export const AddToCartContext = React.createContext<IAddToCartContext>(
  {} as IAddToCartContext
);

export const RecipesResults = ({
  useQueryResult,
  selectable,
  selectedRecipesArray,
  setSelectedRecipesArray
}: RecipesResultsProps): JSX.Element => {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const isGreaterThanTablet = useMediaQuery(theme.breakpoints.up("md"));

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isFolderDrawerOpen, setIsFolderDrawerOpen] = useState<boolean>(false);
  const [drawerData, setDrawerData] = useState<Pick<
    Recipe,
    "pk" | "convenience_level"
  > | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const {
    fetchNextPage,
    fetchPreviousPage,
    isLoading,
    isFetching,
    data,
    isError
  } = useQueryResult;

  const totalPages = Math.ceil(
    data?.pages ? data.pages[0].count / (isGreaterThanTablet ? 8 : 4) : 0
  );
  const isRecipeSelected = (
    recipeId: Recipe["pk"],
    recipesArray: Recipe["pk"][]
  ) => {
    if (!selectable) return;

    return Boolean(recipesArray?.find(el => el === recipeId));
  };

  const handleSelect = (recipeId: Recipe["pk"]) => {
    if (!selectable) return;
    if (isRecipeSelected(recipeId, selectedRecipesArray)) {
      return setSelectedRecipesArray(
        selectedRecipesArray.filter(el => el !== recipeId)
      );
    }

    setSelectedRecipesArray([...selectedRecipesArray, recipeId]);
  };

  const handleLeftArrowClick = () => {
    if (currentPage < 1) return;

    fetchPreviousPage();
    setCurrentPage(currentPage - 1);
  };
  const handleRightArrowClick = () => {
    if (currentPage === totalPages - 1) return;

    fetchNextPage();
    setCurrentPage(currentPage + 1);
  };

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
  };

  const handleOpenFolderDrawer = () => {
    setIsFolderDrawerOpen(true);
  };

  return (
    <AddToCartContext.Provider
      value={{ handleOpenDrawer, setDrawerData, handleOpenFolderDrawer }}>
      <Box
        sx={{
          display: "flex",
          flexFlow: "column",
          rowGap: 2.5,
          mb: 2
        }}>
        <Box
          sx={{
            py: 3,
            minHeight: 450,
            flex: 1,
            display: "flex",
            columnGap: 2,
            rowGap: 8,
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
            "& .spinner,.error": {
              alignSelf: "center !important"
            },
            "&>:nth-of-type(odd)": {
              display: "flex",
              alignItems: "start",
              justifyContent: {
                sm: "center"
              }
            },
            "&>:nth-of-type(even)": {
              display: "flex",
              alignItems: "end",
              justifyContent: {
                sm: "center"
              }
            }
          }}>
          {/*@ts-ignore*/}
          {isFetching && !data?.pages[currentPage] && (
            <CircularProgress className="spinner" />
          )}
          {!isLoading &&
            data?.pages[currentPage] &&
            data?.pages[currentPage].results.map((recipe, index) => (
              <Box
                id={`recipe_results-element-${index}`}
                data-qa={"recipe_results-element"}
                className="result_element"
                sx={{
                  height: 115 * 1.5,
                  width: "min-content",
                  minWidth: { xs: "40%", lg: "23%" }
                }}
                key={recipe.pk}>
                {selectable ? (
                  <RecipePanSelectable
                    key={recipe.pk}
                    recipe={recipe}
                    onSelect={handleSelect}
                    selected={
                      isRecipeSelected(
                        recipe.pk,
                        selectedRecipesArray
                      ) as boolean
                    }
                  />
                ) : (
                  <RecipePan key={recipe.pk} recipe={recipe} />
                )}
              </Box>
            ))}

          {isError && !data?.pages[currentPage] && (
            <Box className="error">
              <Typography color={"#a1a1a1"} fontSize={20} fontWeight={500}>
                {t("somethingWentWrong")}
              </Typography>
            </Box>
          )}
          {!isError && data && !(data?.pages?.[0]?.results?.length > 0) && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center !important",
                justifyContent: "center"
              }}>
              <NotFoundIcon width={100} height={100} />

              <Box className="error">
                <Typography color={"#a1a1a1"} fontSize={20} fontWeight={500}>
                  {t("no-results")}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        {!isError &&
          data?.pages[currentPage] &&
          data.pages[currentPage]?.count !== 0 && (
            <Box
              sx={{
                height: 36,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                px: 2
              }}>
              <Box
                fontWeight={400}
                sx={{
                  ml: 0,
                  mr: "auto",
                  display: "flex",
                  gap: 0.5,
                  fontSize: { xs: 16, md: 24 }
                }}>
                {currentPage + 1}
                <Box
                  component={"span"}
                  color="#D0D5DD"
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography>/</Typography>
                  {isLoading ? (
                    <CircularProgress sx={{ my: "auto" }} size={14} />
                  ) : (
                    totalPages
                  )}
                </Box>
              </Box>
              <ArrowButton
                onClick={handleLeftArrowClick}
                disabled={isLoading || currentPage === 0}
                icon={isGreaterThanTablet ? ArrowLeftLong : ArrowLeftSmall}
              />
              <ArrowButton
                onClick={handleRightArrowClick}
                disabled={isLoading || currentPage === totalPages - 1}
                icon={isGreaterThanTablet ? ArrowRightLong : ArrowRightSmall}
              />
            </Box>
          )}
      </Box>
      <AddToCartDrawer
        isOpen={isDrawerOpen}
        toggleDrawer={setIsDrawerOpen}
        recipeData={drawerData}
      />
      <AddToFolderDrawer
        isOpen={isFolderDrawerOpen}
        toggleDrawer={setIsFolderDrawerOpen}
        recipeId={drawerData?.pk}
      />
    </AddToCartContext.Provider>
  );
};
